import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { once } from '@ember/runloop';
import { cached } from '@glimmer/tracking';

import moment from 'moment';

import type { CustomPeriodPickerOptions } from 'webapp-types';
import { TimeUnit } from 'webapp/enums/enums';
import { equal } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormCustomPeriodPickerComponent from 'webapp/components/form/custom-period-picker';
import FormDatetimePickerComponent from 'webapp/components/form/datetime-picker';
import FormInputIntegerComponent from 'webapp/components/form/input-integer';
import type { Args as BaseArgs } from 'webapp/components/form/range/list-item-base';
import FormRangeListItemBaseComponent from 'webapp/components/form/range/list-item-base';
import type { OptionRaw } from 'webapp/components/form/select';
import FormSelectComponent from 'webapp/components/form/select';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import DateRange from 'webapp/models-custom/date-range';

export interface Args extends BaseArgs {
	model: DateRange;
	customPeriodPickerOptions?: CustomPeriodPickerOptions;
}

export default class FormRangeListItemComponent extends FormRangeListItemBaseComponent<Args> {
	// Additional options:
	// Today: include future option
	// Between: datepicker with rolling option
	// Last X: unit and multiplier picker and include current option

	override get hasExtra() {
		// for today, if we are hiding checkbox, treat it as we do NOT have extra
		return (
			this.args.model.hasExtraSettings &&
			(!this.args.model.isToday || !this.hideIncludeFutureCheckbox)
		);
	}

	get showLastXOptions() {
		return this.args.isSelected && this.args.model.isLastX;
	}

	get showSinceOptions() {
		return this.args.isSelected && this.args.model.isSince;
	}

	get hasOptionsNextToTitle() {
		return this.showLastXOptions || this.showSinceOptions;
	}

	get hasBorderAbove() {
		// show border above last x and above WTD (but not in popular category)
		return (
			this.args.model.isLastX ||
			(this.args.model.rangeTypeId === 'WeekToDate' &&
				this.args.model.rangeCategoryId === DateRange.Category.CURRENT)
		);
	}

	// ------------- Today only -------------

	private _includeFutureDisabled = false;

	@cached
	get includeFutureDisabled() {
		const [limitation1, limitation2] = this.args.model.limitations || [],
			disabled = limitation1?.disabled || limitation2?.disabled;

		if (this._includeFutureDisabled !== disabled) {
			this._includeFutureDisabled = !!disabled;
			once(this, this.checkSelectedIncludeFuture);
		}

		return this._includeFutureDisabled;
	}

	get hideIncludeFutureCheckbox() {
		// don't show empty extra checkbox when disabled
		return (
			this.args.hideUnsupported &&
			this.includeFutureDisabled &&
			!this.args.model.rangeIncludeFuture
		);
	}

	@cached
	get includeFutureInfoText() {
		let text;
		if (this.includeFutureDisabled) {
			text = (this.args.model.limitations || []).find((item) => item.disabled)?.infoText;
		}
		return text;
	}

	// includeFuture switching if selected is not supported
	@action
	checkSelectedIncludeFuture() {
		if (this.includeFutureDisabled && this.args.model.isToday) {
			const limitation1 = (this.args.model.limitations || [])[0];

			const allowedIncludeFuture =
				limitation1 && limitation1.id.indexOf('ToHour') > 0
					? limitation1.disabled
					: !limitation1?.disabled;

			this.args.model.updatePropIfChanged('rangeIncludeFuture', allowedIncludeFuture);
		}
	}

	// ------------- LastX & SinceToDate commons ------------

	private _includeCurrentDisabled = false;

	// includeCurrent restrictions
	get limitations() {
		if (this.args.model.isLastX) {
			return this.selectedLastXLimitations;
		} else {
			return this.selectedSinceLimitations;
		}
	}

	@cached
	get includeCurrentDisabled() {
		const [limitation1, limitation2] = this.limitations,
			disabled = limitation1?.disabled || limitation2?.disabled;

		if (this._includeCurrentDisabled !== disabled) {
			this._includeCurrentDisabled = !!disabled;
			once(this, this.checkSelectedIncludeCurrent);
		}

		return disabled;
	}

	get hideIncludeCurrentCheckbox() {
		// don't show empty extra checkbox when disabled
		return (
			this.args.hideUnsupported &&
			this.includeCurrentDisabled &&
			!this.args.model.rangeIncludeCurrent
		);
	}

	@cached
	get includeCurrentInfo() {
		let limitation, info;
		if (this.includeCurrentDisabled) {
			limitation = this.limitations.find((item) => item.disabled);
		}
		if (limitation) {
			info = this.mmMetricsService.getLimitationInfoForDateRangeOrGranulation(
				limitation,
				this.currentUserService.isAdmin,
			);
		}
		return info;
	}

	// includeCurrent switching if selected is not supported
	@action
	checkSelectedIncludeCurrent() {
		if (this.includeCurrentDisabled) {
			const [limitation1] = this.limitations;

			const allowedIncludeCurrent =
				limitation1?.disabled && limitation1.id.indexOf('WithoutCurrent') > 0;

			this.args.model.updatePropIfChanged('rangeIncludeCurrent', allowedIncludeCurrent);
		}
	}

	@cached
	get rangeIncludeCurrentLabel() {
		let unitName;
		switch (this.args.model.rangeTimeUnit) {
			case TimeUnit.DAY:
				unitName = 'today';
				break;
			case TimeUnit.WEEK:
				unitName = 'this week';
				break;
			case TimeUnit.MONTH:
				unitName = 'this month';
				break;
			case TimeUnit.QUARTER:
				unitName = 'this quarter';
				break;
			case TimeUnit.YEAR:
				unitName = 'this year';
				break;
			default:
				unitName = 'future';
				break;
		}
		return `Include ${unitName}`;
	}

	// ------------- SinceToDate only ------------

	@cached
	get selectedSinceLimitations() {
		// currently selected Since limitations (dont take into account includeCurrent)
		const limitation1 = (this.args.model.limitations || []).find(
				(item) => item.id === 'SinceToDate',
			)!,
			limitation2 = (this.args.model.limitations || []).find(
				(item) => item.id === 'SinceToDateWithoutCurrent',
			)!;
		return [limitation1, limitation2];
	}

	@action
	onSinceStartDateChange(startDate: string) {
		if (this.args.model.rangeStartDate !== startDate) {
			this.args.model.rangeStartDate = startDate;
			this.checkSinceTodayCase();
		}
	}

	private checkSinceTodayCase() {
		// "since today" + include today: false cannot work together
		// in this case, move "since" date to yesterday
		if (!this.args.model.rangeIncludeCurrent) {
			const selected = moment(this.args.model.rangeStartDate, this.customDateFormat);
			if (selected.isSame(moment(), 'day')) {
				const yesterdayDate = selected.add(-1, 'day').format(this.customDateFormat);
				this.args.model.rangeStartDate = yesterdayDate;
			}
		}
	}

	// ------------- LastX only ------------

	private _timeUnits: OptionRaw<number>[] = [];

	@cached
	get timeUnits(): OptionRaw<number>[] {
		const granulations = DateRange.baseGranulations
			.filter((item) => item.id !== TimeUnit.TOTAL)
			.filter((item) => item.id !== TimeUnit.HOUR);

		let timeUnits;
		if (this.args.model.isLastX) {
			timeUnits = granulations.map((timeUnit) => {
				const [limitation1, limitation2] = this.getLastXLimitationsForGranulation(
						timeUnit.id,
					),
					disabled = limitation1?.disabled && limitation2?.disabled;
				let tooltipText;

				// if the unit is disabled, show that on tooltip in dropdown
				if (disabled) {
					if (limitation1.infoText === limitation2.infoText) {
						tooltipText = limitation1.infoText;
					} else {
						tooltipText = [limitation1, limitation2]
							.map((limitation) => {
								return `[${limitation.id}] ${limitation.infoText}`;
							})
							.join('<br>');
					}
					if (tooltipText) {
						const iconName =
							limitation1.isPaidLimitation && limitation2.isPaidLimitation
								? 'Lock'
								: 'Question';
						const iconClass =
							limitation1.isPaidLimitation && limitation2.isPaidLimitation
								? ' class="green"'
								: '';
						tooltipText = `<ui-icon name="${iconName}"${iconClass} title="${tooltipText}"></ui-icon>`;
					}
				}

				return {
					value: timeUnit.id,
					label: `${timeUnit.title.toLowerCase()}s`,
					label3: tooltipText,
					disabled,
				};
			});
		} else {
			timeUnits = granulations.map((timeUnit) => {
				return {
					value: timeUnit.id,
					label: timeUnit.title,
					disabled: false,
				};
			});
		}

		if (this.args.hideUnsupported) {
			timeUnits = timeUnits.filter((tu) => !tu.disabled);
		}

		if (!equal(this._timeUnits, timeUnits)) {
			this._timeUnits = timeUnits;
			once(this, this.checkSelectedTimeUnit);
		}

		return this._timeUnits;
	}

	private getLastXLimitationsForGranulation(timeUnitId: number) {
		// helper fn to get both limitations for given granularity for lastX types (dont take into account includeCurrent)
		const timeUnit = DateRange.baseGranulations.find((item) => item.id === timeUnitId),
			limitation1 = (this.args.model.limitations || []).find(
				(item) => item.id === `LastX${timeUnit?.title}s`,
			)!,
			limitation2 = (this.args.model.limitations || []).find(
				(item) => item.id === `LastX${timeUnit?.title}sWithoutCurrent`,
			)!;
		return [limitation1, limitation2];
	}

	@cached
	get selectedLastXLimitations() {
		// currently selected lastX limitations (dont take into account includeCurrent)
		return this.getLastXLimitationsForGranulation(this.args.model.rangeTimeUnit as number);
	}

	get showMaxMultiplierInfoText() {
		return this.maxMultiplier && !this.multiplierIsDropdown;
	}

	@cached
	get selectedLastXLimitationForTimeUnit() {
		// currently selected lastX limitation (take into account includeCurrent)
		return (this.args.model.limitations || []).find(
			(item) => item.id === this.args.model.rangeTypeId,
		);
	}

	// timeUnit switching if selected is not supported
	@action
	checkSelectedTimeUnit() {
		const selectedTimeUnit = this.timeUnits.find(
			(item) => item.value === (this.args.model.rangeTimeUnit as number),
		);
		if (!selectedTimeUnit || selectedTimeUnit.disabled) {
			const firstAvailable = this.timeUnits.find((tu) => !tu.disabled)?.value;
			if (firstAvailable) {
				this.args.model.updatePropIfChanged('rangeTimeUnit', firstAvailable);
			}
		}
	}

	// rangeMultiplier restrictions (only specific multipliers allowed)
	get multipliersRestrictions() {
		return this.selectedLastXLimitationForTimeUnit?.rawData?.allowedMultipliers;
	}

	override get maxMultiplier() {
		return this.selectedLastXLimitationForTimeUnit?.rawData?.maxMultiplier;
	}

	get multiplierIsDropdown() {
		return !!this.multipliersRestrictions;
	}

	private _multiplierOptions: LabelValuePairNumeric[] = [];

	get multiplierOptions() {
		const options = (this.multipliersRestrictions || []).map((multiplier: number) => {
			return {
				value: multiplier,
				label: String(multiplier),
			};
		});

		if (!equal(this._multiplierOptions, options)) {
			this._multiplierOptions = options;
			once(this, this.checkSelectedMultiplier);
		}

		return this._multiplierOptions;
	}

	// rangeMultiplier switching if selected is not supported
	@action
	checkSelectedMultiplier() {
		if (this.multiplierIsDropdown) {
			const selectedMultiplier = this.multiplierOptions.find(
				(item) => item.value === this.args.model.rangeMultiplier!,
			);
			if (!selectedMultiplier) {
				const firstAvailable = this.multiplierOptions[0]?.value;
				this.args.model.updatePropIfChanged('rangeMultiplier', firstAvailable);
			}
		}
	}

	// ------------- Between only -------------

	customDateFormat = DateRange.customDateFormat;

	@cached
	get isRollingDisabled() {
		const rollingLimitation = (this.args.model.limitations || []).find(
			(item) => item.id === 'LastXDaysRolling',
		);
		return !!rollingLimitation && rollingLimitation.disabled;
	}

	@cached
	get isCustomRangeDisabled() {
		const customLimitation = (this.args.model.limitations || []).find(
			(item) => item.id === 'CustomRange',
		);
		return !!customLimitation && customLimitation.disabled;
	}

	@cached
	get customInfo() {
		const limitations = [
			(this.args.model.limitations || []).find((item) => item.id === 'LastXDaysRolling'),
			(this.args.model.limitations || []).find((item) => item.id === 'CustomRange'),
		];

		let info;

		const disabledLimitation = limitations.find((l) => l?.disabled);

		if (disabledLimitation) {
			info = this.mmMetricsService.getLimitationInfoForDateRangeOrGranulation(
				disabledLimitation,
				this.currentUserService.isAdmin,
			);
		}

		return info;
	}

	private _isRollingCheckboxDisabled = false;

	get isRollingCheckboxDisabled() {
		const disabled = this.isRollingDisabled || this.isCustomRangeDisabled;

		if (this._isRollingCheckboxDisabled !== disabled) {
			this._isRollingCheckboxDisabled = disabled;
			once(this, this.checkSelectedIsRolling);
		}

		return disabled;
	}

	get hideIsRollingCheckbox() {
		// don't show empty extra checkbox when disabled
		return (
			!!this.args.hideUnsupported &&
			this.isRollingCheckboxDisabled &&
			!this.args.model.isRolling
		);
	}

	// isRolling switching if selected is not supported
	@action
	checkSelectedIsRolling() {
		if (this.isRollingCheckboxDisabled) {
			const allowedRolling = this.isCustomRangeDisabled;

			this.args.model.updatePropIfChanged('isRolling', allowedRolling);
		}
	}

	// ------------- Selected only -------------

	@action
	checkAndResetExtraProps() {
		if (this.showExtra) {
			// only for selected
			if (this.showLastXOptions) {
				this.checkSelectedTimeUnit();
				this.checkSelectedIncludeCurrent();
				this.checkSelectedMultiplier();
			}

			if (this.showLastXOptions) {
				this.checkSelectedIncludeCurrent();
			}

			if (this.args.model.isToday) {
				this.checkSelectedIncludeFuture();
			}

			if (this.args.model.isCustom) {
				this.checkSelectedIsRolling();
			}
		}
	}

	@action
	onRangeMultiplierChange(value: number) {
		this.args.model.rangeMultiplier = value;
	}

	@action
	onRangeTimeUnitChange(value: TimeUnit) {
		this.args.model.rangeTimeUnit = value;
	}

	@action
	onRangeIncludeFutureChange(rangeIncludeFuture: boolean) {
		this.args.model.rangeIncludeFuture = rangeIncludeFuture;
	}

	@action
	onRangeIncludeCurrentChange(rangeIncludeCurrent: boolean) {
		this.args.model.rangeIncludeCurrent = rangeIncludeCurrent;
		if (this.args.model.isSince) {
			// if we have today selected but have removed include current, autoswitch to yesterday
			this.checkSinceTodayCase();
		}
	}

	@action
	onRollingChange(isRolling: boolean) {
		this.args.model.isRolling = isRolling;
	}

	@action
	onCustomPeriodChange(startDate: string, endDate: string, isRolling: boolean) {
		this.args.model.rangeStartDate = startDate;
		this.args.model.rangeEndDate = endDate;

		this.onRollingChange(isRolling);
	}

	@action
	getMaxMultiplierWarningText(type: string, value: number | undefined) {
		return this._getMaxMultiplierWarningText(type, value) || '';
	}

	@action
	confirmAndClose() {
		this.args.onSelect(this.args.model);
	}
	<template>
		<li
			class={{joinClasses
				(array
					"item-ct"
					(if @isSelected "selected")
					(if @model.isDisabled "disabled")
					this.limitationInfo.className
					(if this.hasOptionsNextToTitle "has-options-next-to-title")
					(if @model.isCustom "is-datepicker")
					(if @model.isAllTime "is-all-time")
					(if this.hasBorderAbove "border-above")
				)
			}}
			{{didUpdate this.checkAndResetExtraProps @isSelected}}
			{{didUpdate this.checkSelectedIncludeFuture @model.rangeIncludeFuture}}
			{{didUpdate this.checkSelectedTimeUnit @model.rangeTimeUnit}}
			{{didUpdate this.checkSelectedIncludeCurrent @model.rangeIncludeCurrent}}
			{{didUpdate this.checkSelectedMultiplier @model.rangeMultiplier}}
			{{didUpdate this.checkSelectedIsRolling @model.isRolling}}
		>
			<div class="item">
				<a class="item-title" {{on "click" this.select}}>
					{{#if @isSelected}}
						<CommonUiIconComponent
							@name="CheckboxTick"
							@size="xs"
							@class="selected-icon"
						/>
					{{/if}}
					<span>
						{{@model.rangeTitle}}
						{{#if this.showInfoTextAtTitle}}
							<ToolTipComponent
								@text={{this.limitationInfo.text}}
								@interactive={{true}}
								@maxWidth={{250}}
								@theme={{@theme}}
							/>
						{{/if}}
					</span>
					{{#if this.showInfoTextAtTitle}}
						<ToolTipWithIconComponent
							@text={{this.limitationInfo.text}}
							@maxWidth={{250}}
							@iconName={{this.limitationInfo.icon}}
							@theme={{@theme}}
						/>
					{{/if}}
				</a>
				{{#if this.showExtra}}
					<div class="item-extra">
						{{#if @model.isToday}}
							<div
								class="extra-checkbox
									{{if this.hideIncludeFutureCheckbox 'hidden'}}"
							>
								<FormCheckboxComponent
									@name="includeFuture"
									@selected={{@model.rangeIncludeFuture}}
									@label="Include future"
									@isDisabled={{this.includeFutureDisabled}}
									@onChange={{this.onRangeIncludeFutureChange}}
									@theme="blue2"
									@size="small"
								/>
								{{#if this.includeFutureInfoText}}
									<ToolTipWithIconComponent
										@text={{this.includeFutureInfoText}}
										@maxWidth={{250}}
										@iconName="Question"
										@iconSize="md"
										@theme={{@theme}}
									/>
								{{/if}}
							</div>
						{{/if}}
						{{#if @model.isLastX}}
							<div class="options-next-to-title">
								<div class="extra-field-multiplier">
									{{#if this.multiplierIsDropdown}}
										<FormSelectComponent
											name="multiplier"
											@selected={{@model.rangeMultiplier}}
											@options={{this.multiplierOptions}}
											@onChange={{this.onRangeMultiplierChange}}
											@isRequired={{true}}
											@tooltipOnSelectionFromProperty=""
											@size="tiny"
											@theme={{@theme}}
										/>
									{{else}}
										<FormInputIntegerComponent
											@name="multiplier"
											@onChange={{this.onRangeMultiplierChange}}
											@getErrorText={{this.getMaxMultiplierWarningText}}
											@value={{@model.rangeMultiplier}}
											@min={{2}}
											@max={{this.maxMultiplier}}
											@maxlength={{4}}
											@showWarningOnMaxChange={{false}}
											@size="tiny"
											@theme={{@theme}}
										/>
									{{/if}}
								</div>
								<div class="extra-field-unit">
									<FormSelectComponent
										name="timeUnit"
										@selected={{@model.rangeTimeUnit}}
										@options={{this.timeUnits}}
										@onChange={{this.onRangeTimeUnitChange}}
										@isRequired={{true}}
										@tooltipOnSelectionFromProperty=""
										@size="tiny"
										@theme={{@theme}}
									/>
									{{#if this.showMaxMultiplierInfoText}}
										<ToolTipWithIconComponent
											@text={{this.maxMultiplierWarningText}}
											@maxWidth={{250}}
											@iconName="Question"
											@iconSize="md"
											@theme={{@theme}}
										/>
									{{/if}}
								</div>
							</div>
							<div
								class="extra-checkbox
									{{if this.hideIncludeFutureCheckbox 'hidden'}}"
							>
								<FormCheckboxComponent
									@name="includeCurrent"
									@selected={{@model.rangeIncludeCurrent}}
									@label={{this.rangeIncludeCurrentLabel}}
									@isDisabled={{this.includeCurrentDisabled}}
									@onChange={{this.onRangeIncludeCurrentChange}}
									@theme="blue2"
									@size="small"
								/>
								{{#if this.includeCurrentInfo}}
									<ToolTipWithIconComponent
										@text={{this.includeCurrentInfo.text}}
										@maxWidth={{250}}
										@iconName={{this.includeCurrentInfo.icon}}
										@theme={{@theme}}
									/>
								{{/if}}
							</div>
						{{/if}}
						{{#if @model.isSince}}
							<div class="options-next-to-title">
								<FormDatetimePickerComponent
									@value={{@model.rangeStartDate}}
									@dateFormat={{this.customDateFormat}}
									@onChange={{this.onSinceStartDateChange}}
									@disableTimePicker={{true}}
									@disableFuture={{true}}
									@theme={{@theme}}
									@size="tiny"
								/>
							</div>
							<div
								class="extra-checkbox
									{{if this.hideIncludeFutureCheckbox 'hidden'}}"
							>
								<FormCheckboxComponent
									@name="includeCurrent"
									@selected={{@model.rangeIncludeCurrent}}
									@label={{this.rangeIncludeCurrentLabel}}
									@isDisabled={{this.includeCurrentDisabled}}
									@onChange={{this.onRangeIncludeCurrentChange}}
									@theme="blue2"
									@size="small"
								/>
								{{#if this.includeCurrentInfo}}
									<ToolTipWithIconComponent
										@text={{this.includeCurrentInfo.text}}
										@maxWidth={{250}}
										@iconName={{this.includeCurrentInfo.icon}}
										@theme={{@theme}}
									/>
								{{/if}}
							</div>
						{{/if}}
						{{#if @model.isCustom}}
							<div class="extra-datepicker">
								<FormCustomPeriodPickerComponent
									@format={{this.customDateFormat}}
									@startValue={{@model.rangeStartDate}}
									@endValue={{@model.rangeEndDate}}
									@isRolling={{@model.isRolling}}
									@isRollingCheckboxDisabled={{this.isRollingCheckboxDisabled}}
									@isRollingCheckboxHidden={{this.hideIsRollingCheckbox}}
									@onChange={{this.onCustomPeriodChange}}
									@onOk={{this.confirmAndClose}}
									@customPeriodPickerOptions={{@customPeriodPickerOptions}}
									@theme={{@theme}}
									@size="tiny"
								/>
							</div>
							<div class="extra-checkbox {{if this.hideIsRollingCheckbox 'hidden'}}">
								<FormCheckboxComponent
									@name="isRolling"
									@selected={{@model.isRolling}}
									@label="Rolling range"
									@isDisabled={{this.isRollingCheckboxDisabled}}
									@onChange={{this.onRollingChange}}
									@theme="blue2"
									@size="small"
								/>
								<ToolTipWithIconComponent
									@text="Dates adjust relative to current date."
									@maxWidth={{250}}
									@theme={{@theme}}
									@iconName="Question"
									@iconSize="md"
								/>
								{{#if this.customInfo}}
									<ToolTipWithIconComponent
										@text={{this.customInfo.text}}
										@maxWidth={{250}}
										@iconName={{this.customInfo.icon}}
										@theme={{@theme}}
									/>
								{{/if}}
							</div>
						{{/if}}
						<a class="apply" {{on "click" this.confirmAndClose}}>OK</a>
					</div>
				{{/if}}
			</div>
		</li>
	</template>
}
